import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { useStandards } from "hooks/useStandards/useStandards";
import { buildSelectOptions } from "utils/standards";
import { buildPreviewChallenge } from "utils/challenge";
import { constants, generateRoutePath, colors } from "utils";
import { ConsensusActivityRender } from "../../Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { EditConsensusQuestionModal } from "../../Session/Activity/activities/ConsensusActivity/Edit/EditConsensusQuestionModal";
import { EvaluatorQuestionDetail } from "./EvaluatorQuestionDetail";
import "./evaluatorPreview.css";
import {
  REVIEW_STATUS_FILTER_OPTIONS,
  QUESTION_TYPE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TRIBOOL_FILTER_OPTIONS,
  fetchEvaluatorQuestions,
  fetchEvaluatorSources,
  listEvaluationBatches,
  isEndpointMissing,
} from "./evaluatorApi";

const Toggle = ({ label, checked, onChange, hint }) => (
  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span style={{ fontWeight: 600 }}>{label}</span>
    {hint && <span style={{ fontSize: "0.8em", color: "#999" }}>{hint}</span>}
  </label>
);

const selectStyles = {
  control: (css) => ({ ...css, width: "100%" }),
  // The select sits inside a column-direction FilterField, so a vertical
  // flex-basis here would stretch it tall. Keep it auto-height, full width.
  container: (css) => ({ ...css, width: "100%" }),
  menuPortal: (css) => ({ ...css, zIndex: 9999 }),
};

const FilterField = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 220px", minWidth: "200px" }}>
    <span
      style={{
        textTransform: "uppercase",
        fontSize: "0.65em",
        fontWeight: 700,
        letterSpacing: "0.05em",
        color: "#999",
      }}
    >
      {label}
    </span>
    {children}
  </div>
);

export const EvaluatorReview = () => {
  const { authUserData, loading } = useAuthUserData();
  const history = useHistory();
  const standardsData = useStandards();

  const [blindReview, setBlindReview] = useState(true);

  const [reviewStatus, setReviewStatus] = useState("all");
  const [questionId, setQuestionId] = useState("");
  const [ccss, setCcss] = useState("");
  const [learningComponent, setLearningComponent] = useState("");
  const [imLesson, setImLesson] = useState("");
  const [questionType, setQuestionType] = useState("all");
  const [status, setStatus] = useState("all");
  const [itemSource, setItemSource] = useState("");
  const [wordProblem, setWordProblem] = useState("all");
  const [illustration, setIllustration] = useState("all");
  const [equationCentered, setEquationCentered] = useState("all");

  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchesPending, setBatchesPending] = useState(false);

  // Item Source options come from GET /admin/evaluator/sources. If that
  // endpoint isn't available yet (404), fall back to the free-text input.
  const [sources, setSources] = useState([]);
  const [sourcesPending, setSourcesPending] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [endpointPending, setEndpointPending] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [challengeToEdit, setChallengeToEdit] = useState(null);
  const [needsRefresh, setNeedsRefresh] = useState(false);

  useEffect(() => {
    listEvaluationBatches()
      .then((res) => setBatches(res?.batches || []))
      .catch((e) => {
        if (isEndpointMissing(e)) setBatchesPending(true);
      });
  }, []);

  useEffect(() => {
    fetchEvaluatorSources()
      .then((res) => setSources(res?.sources || []))
      .catch((e) => {
        if (isEndpointMissing(e)) setSourcesPending(true);
      });
  }, []);

  const sourceOptions = useMemo(() => {
    const options = (sources || []).map((s) => ({ value: s, label: s }));
    options.unshift({ value: "", label: "All sources" });
    return options;
  }, [sources]);

  const buildFilters = () => {
    const filters = {
      review_status: reviewStatus,
      question_id: questionId.trim(),
      ccss: ccss.trim(),
      learning_component: learningComponent.trim(),
      im_lesson: imLesson.trim(),
      question_type: questionType,
      question_status: status,
      source: itemSource.trim(),
      word_problem: wordProblem,
      illustration,
      equation_centered: equationCentered,
    };
    // The batch dropdown is the single queue control: a non-null value scopes
    // to a saved batch; "All Questions" (null) is free-browse via filters.
    if (selectedBatch) filters.batch_id = selectedBatch;
    return filters;
  };

  const runSearch = async () => {
    setIsLoading(true);
    setFetchError("");
    setEndpointPending(false);
    try {
      const res = await fetchEvaluatorQuestions(buildFilters());
      const fetched = res?.items || [];
      setItems(fetched);
      setTotal(res?.total ?? fetched.length);
      setSelectedId(fetched.length ? fetched[0].id : null);
    } catch (e) {
      if (isEndpointMissing(e)) {
        setEndpointPending(true);
        setItems([]);
        setTotal(0);
      } else {
        setFetchError("Failed to load questions.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (needsRefresh) {
      runSearch();
      setNeedsRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRefresh]);

  const standardOptions = useMemo(() => {
    const options = buildSelectOptions(standardsData);
    options.unshift({ value: "", label: "All CCSS standards" });
    return options;
  }, [standardsData]);

  const batchOptions = useMemo(() => {
    // The batches endpoint returns {id, name, item_count}; the option value is
    // the batch UUID, passed back as batch_id to filter the questions list.
    const options = (batches || []).map((b) => ({
      value: b.id,
      label: b.name + (b.item_count != null ? ` (${b.item_count})` : ""),
    }));
    // Default option: no batch filter / free-browse the full question pool.
    options.unshift({ value: null, label: "All Questions" });
    return options;
  }, [batches]);

  const selectedItem = items.find((q) => q.id === selectedId) || null;

  // The question prompt can carry inline LaTeX (\(..\)); the reused render path
  // doesn't always trigger MathJax, so typeset the preview when the selection
  // changes (also converts \$ -> $).
  const previewRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const mj = window.MathJax;
      try {
        if (mj.typesetPromise) mj.typesetPromise();
        else mj.typeset();
      } catch (e) {
        /* ignore */
      }
    };
    const ready = () => {
      const mj = typeof window !== "undefined" ? window.MathJax : null;
      if (!mj || !(mj.typesetPromise || mj.typeset)) return false;
      if (mj.startup && mj.startup.promise && mj.startup.promise.then) {
        mj.startup.promise.then(run);
      } else {
        run();
      }
      return true;
    };
    if (ready()) return () => { cancelled = true; };
    let n = 0;
    const iv = setInterval(() => {
      if (cancelled || ready() || ++n > 20) clearInterval(iv);
    }, 150);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [selectedId]);

  const onEdit = (challenge) => {
    setChallengeToEdit(challenge);
    setIsEditOpen(true);
  };

  if (loading) return <></>;
  if (authUserData.isAnonymous) {
    history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
    return <></>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }}>
        <Toggle
          label="Blind Review"
          checked={blindReview}
          onChange={setBlindReview}
          hint="ON: hide evaluator-authored tags until you judge"
        />
      </div>

      {/* The batch dropdown is the single queue control. "All Questions"
          (default) = free-browse via filters; selecting a saved batch scopes
          the list to that batch. Plain block wrapper so the FilterField's
          row-oriented flex-basis doesn't stretch vertically here. */}
      <div style={{ maxWidth: "640px" }}>
        <FilterField label="Batch">
          {batchesPending ? (
            <Select
              isDisabled={isLoading}
              options={[{ value: null, label: "All Questions" }]}
              value={{ value: null, label: "All Questions" }}
              styles={selectStyles}
            />
          ) : (
            <Select
              isDisabled={isLoading}
              isSearchable
              placeholder="All Questions"
              options={batchOptions}
              value={batchOptions.find((o) => o.value === selectedBatch) || batchOptions[0] || null}
              onChange={(t) => setSelectedBatch(t.value)}
              styles={selectStyles}
            />
          )}
        </FilterField>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
        <FilterField label="Review Status">
          <Select
            isDisabled={isLoading}
            options={REVIEW_STATUS_FILTER_OPTIONS}
            value={REVIEW_STATUS_FILTER_OPTIONS.find((o) => o.value === reviewStatus)}
            onChange={(t) => setReviewStatus(t.value)}
            styles={selectStyles}
          />
        </FilterField>
        <FilterField label="OKO Question ID">
          <input
            type="text"
            value={questionId}
            placeholder="UUID…"
            onChange={(e) => setQuestionId(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </FilterField>
        <FilterField label="CCSS (code or name)">
          <Select
            isDisabled={isLoading}
            isSearchable
            options={standardOptions}
            value={standardOptions.find((o) => o.value === ccss) || null}
            onChange={(t) => setCcss(t.value)}
            styles={selectStyles}
          />
        </FilterField>
        <FilterField label="Learning Component (id or name)">
          <input
            type="text"
            value={learningComponent}
            placeholder="LC id or name…"
            onChange={(e) => setLearningComponent(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </FilterField>
        <FilterField label="IM Lesson (unit or lesson)">
          <input
            type="text"
            value={imLesson}
            placeholder="Unit / lesson…"
            onChange={(e) => setImLesson(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </FilterField>
        <FilterField label="Question Type">
          <Select
            isDisabled={isLoading}
            options={QUESTION_TYPE_FILTER_OPTIONS}
            value={QUESTION_TYPE_FILTER_OPTIONS.find((o) => o.value === questionType)}
            onChange={(t) => setQuestionType(t.value)}
            styles={selectStyles}
          />
        </FilterField>
        <FilterField label="Status">
          <Select
            isDisabled={isLoading}
            options={STATUS_FILTER_OPTIONS}
            value={STATUS_FILTER_OPTIONS.find((o) => o.value === status)}
            onChange={(t) => setStatus(t.value)}
            styles={selectStyles}
          />
        </FilterField>
        <FilterField label="Item Source">
          {sourcesPending ? (
            <input
              type="text"
              value={itemSource}
              placeholder="WrenEd, ASSISTments…"
              onChange={(e) => setItemSource(e.target.value)}
              style={{ padding: "7px 10px", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          ) : (
            <Select
              isDisabled={isLoading}
              isSearchable
              placeholder="All sources"
              options={sourceOptions}
              value={sourceOptions.find((o) => o.value === itemSource) || sourceOptions[0]}
              onChange={(t) => setItemSource(t.value)}
              styles={selectStyles}
            />
          )}
        </FilterField>
        <FilterField label="Word Problem">
          <Select
            isDisabled={isLoading}
            options={TRIBOOL_FILTER_OPTIONS}
            value={TRIBOOL_FILTER_OPTIONS.find((o) => o.value === wordProblem)}
            onChange={(t) => setWordProblem(t.value)}
            styles={selectStyles}
          />
        </FilterField>
        <FilterField label="Illustration">
          <Select
            isDisabled={isLoading}
            options={TRIBOOL_FILTER_OPTIONS}
            value={TRIBOOL_FILTER_OPTIONS.find((o) => o.value === illustration)}
            onChange={(t) => setIllustration(t.value)}
            styles={selectStyles}
          />
        </FilterField>
        <FilterField label="Equation-centered">
          <Select
            isDisabled={isLoading}
            options={TRIBOOL_FILTER_OPTIONS}
            value={TRIBOOL_FILTER_OPTIONS.find((o) => o.value === equationCentered)}
            onChange={(t) => setEquationCentered(t.value)}
            styles={selectStyles}
          />
        </FilterField>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <button
          onClick={runSearch}
          disabled={isLoading}
          style={{
            background: colors.nightlyWoods500,
            color: "white",
            border: "none",
            borderRadius: "20px",
            padding: "9px 22px",
            cursor: isLoading ? "not-allowed" : "pointer",
            fontSize: "14px",
          }}
        >
          {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Search"}
        </button>
        <span style={{ color: "#666", fontSize: "0.9em" }}>
          {items.length > 0 ? `Showing ${items.length} of ${total}` : ""}
        </span>
      </div>

      {endpointPending && (
        <div style={{ color: "#b06a00", fontStyle: "italic" }}>
          /admin/evaluator/questions endpoint not available yet — pending backend. The UI is wired
          and will populate once the endpoint is merged.
        </div>
      )}
      {fetchError && <div style={{ color: "#a33" }}>{fetchError}</div>}

      {/* Two-pane results */}
      <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
        {/* Result list (selectable) */}
        <div
          style={{
            flex: "0 0 220px",
            maxHeight: "70vh",
            overflowY: "auto",
            borderRight: "1px solid #eee",
            paddingRight: "8px",
          }}
        >
          {items.map((q) => (
            <div
              key={q.id}
              onClick={() => setSelectedId(q.id)}
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                cursor: "pointer",
                background: q.id === selectedId ? colors.blue50 : "transparent",
                border: q.id === selectedId ? `1px solid ${colors.blue200}` : "1px solid transparent",
                marginBottom: "4px",
                fontSize: "0.8em",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                wordBreak: "break-all",
              }}
            >
              {q.id?.slice(0, 8)}… · {(q.standards || []).join(", ") || "no std"}
            </div>
          ))}
        </div>

        {/* Left: problem render. Right: detail. */}
        {selectedItem && (
          <>
            {/* Question pane grows to absorb any extra width (e.g. when the
                left nav collapses); the metadata pane stays a fixed-ish column
                so the gained space goes to the question, not the boxes. */}
            <div style={{ flex: "100 1 480px", minWidth: 0, display: "flex", justifyContent: "center" }}>
              <div
                ref={previewRef}
                className="evaluator-mc-preview"
                style={{ width: "100%", border: "1px solid #eee", borderRadius: "8px", padding: "12px", background: "#fff" }}
              >
                <ConsensusActivityRender
                  hideQuestion={false}
                  challenge={buildPreviewChallenge(selectedItem)}
                  onMCAnswer={() => {}}
                  onORAnswer={() => {}}
                  isMathJaxSet={false}
                  showPromptFormula
                />
              </div>
            </div>
            <div style={{ flex: "0 1 420px", minWidth: "340px" }}>
              <EvaluatorQuestionDetail
                challenge={selectedItem}
                blindReview={blindReview}
                onEdit={onEdit}
              />
            </div>
          </>
        )}
        {!selectedItem && !isLoading && !endpointPending && (
          <div style={{ color: "#999", padding: "24px" }}>No questions to review yet. Run a search.</div>
        )}
      </div>

      {challengeToEdit && (
        <EditConsensusQuestionModal
          challenge={challengeToEdit}
          isModalOpen={isEditOpen}
          modalTitle="Edit Question"
          setIsModalOpen={setIsEditOpen}
          setNeedsRefresh={setNeedsRefresh}
        />
      )}
    </div>
  );
};
